import React, { useState, useEffect, useCallback, useMemo } from 'react';
import eventBus, { EVENT_NAMES } from '../../utils/event_bus';
import {
  Datagrid,
  TextField,
  FunctionField,
  Pagination,
  useGetList,
  useList,
  ListContextProvider,
} from 'react-admin';
import { Driver } from '../../models/Driver';
import { TruckLength, TruckType } from '../../utils/truck-utils';
import {
  Box,
  Chip,
  TextField as MuiTextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import zhCN from 'date-fns/locale/zh-CN';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useLocation } from 'react-router-dom';

type FilterType = {
  name: string;
  phone: string;
  plate: string;
  type: string;
  distance: number | null;
  lat?: number;
  lon?: number;
  pos_from?: number;
  pos_to?: number;
};

const truckTypeOptions = [
  { value: '', label: '全部' },
  { value: '0', label: '平板' },
  { value: '1', label: '高栏' },
  { value: '2', label: '厢式' },
  { value: '3', label: '自卸' },
  { value: '4', label: '冷藏' },
  { value: '5', label: '爬梯' },
  { value: '6', label: '集装箱' },
  { value: '7', label: '高低板' },
];

const parseQueryNumber = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const SearchForm: React.FC<{
  filters: FilterType;
  setFilters: React.Dispatch<React.SetStateAction<FilterType>>;
  onSearch: () => void;
  onReset: () => void;
  mapLocation: { latitude: number; longitude: number; address: string; } | null;
}> = ({ filters, setFilters, onSearch, onReset, mapLocation }) => {

  const datePickerSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: 'white' },
    },
    '& .MuiInputBase-input': {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
    '& .MuiSvgIcon-root': { color: 'white' },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhCN}>
      <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MuiTextField
              label="姓名"
              size="small"
              fullWidth
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              InputProps={{ style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <MuiTextField
              label="手机号"
              size="small"
              fullWidth
              value={filters.phone}
              onChange={(e) => setFilters({ ...filters, phone: e.target.value })}
              InputProps={{ style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <MuiTextField
              label="车牌号"
              size="small"
              fullWidth
              value={filters.plate}
              onChange={(e) => setFilters({ ...filters, plate: e.target.value })}
              InputProps={{ style: { color: 'white' } }}
              InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                },
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>车型</InputLabel>
              <Select
                value={filters.type}
                label="车型"
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                {truckTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {mapLocation && (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <DatePicker
                      label="开始时间"
                      value={filters.pos_from ? new Date(filters.pos_from) : null}
                      onChange={(date) => {
                        const timestamp = date ? date.getTime() : undefined;
                        setFilters({ ...filters, pos_from: timestamp });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          InputProps: { style: { color: 'white' } },
                          InputLabelProps: { style: { color: 'rgba(255, 255, 255, 0.7)' } },
                        }
                      }}
                      sx={datePickerSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <DatePicker
                      label="结束时间"
                      value={filters.pos_to ? new Date(filters.pos_to) : null}
                      onChange={(date) => {
                        const timestamp = date ? date.getTime() : undefined;
                        setFilters({ ...filters, pos_to: timestamp });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          InputProps: { style: { color: 'white' } },
                          InputLabelProps: { style: { color: 'rgba(255, 255, 255, 0.7)' } },
                        }
                      }}
                      sx={datePickerSx}
                    />
                  </Box>
                </Box>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <MuiTextField
                  label="距离(km)"
                  type="number"
                  size="small"
                  fullWidth
                  value={filters.distance === null ? '' : filters.distance}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    setFilters({ ...filters, distance: value });

                    if (value !== null) {
                      localStorage.setItem('driverSearchDistance', value.toString());
                    } else {
                      localStorage.removeItem('driverSearchDistance');
                    }

                    eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, value);
                  }}
                  InputProps={{ style: { color: 'white' } }}
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    '& .MuiInputBase-input': {
                      paddingTop: '6px',
                      paddingBottom: '6px',
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSearch}
                  startIcon={<SearchIcon />}
                  size="small"
                  sx={{ padding: '6px 16px' }}
                >
                  查询
                </Button>
                <Button
                  variant="outlined"
                  onClick={onReset}
                  startIcon={<ClearIcon />}
                  size="small"
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    padding: '6px 16px',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  重置
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

const DriverListView: React.FC<{ locationLabel?: string }> = ({
  locationLabel = '当前位置',
}) => {
  const location = useLocation();
  const initialQueryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const cachedDistance = localStorage.getItem('driverSearchDistance');
  const initialLat = parseQueryNumber(initialQueryParams.get('lat'));
  const initialLon = parseQueryNumber(initialQueryParams.get('lon'));
  const initialAddress = initialQueryParams.get('address') || '';
  const initialDistanceParam = parseQueryNumber(initialQueryParams.get('distance'));
  const hasInitialLocation = initialLat !== undefined && initialLon !== undefined;
  const initialDistance = hasInitialLocation
    ? initialDistanceParam ?? 50
    : (cachedDistance ? Number(cachedDistance) : null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [centerLabel, setCenterLabel] = useState(locationLabel);

  const [filters, setFilters] = useState<FilterType>({
    name: '',
    phone: '',
    plate: '',
    type: '',
    lat: initialLat,
    lon: initialLon,
    distance: initialDistance,
    pos_from: undefined,
    pos_to: undefined
  });
  const [searchParams, setSearchParams] = useState<FilterType>({
    name: '',
    phone: '',
    plate: '',
    type: '',
    lat: initialLat,
    lon: initialLon,
    distance: hasInitialLocation && initialDistance !== null ? initialDistance * 1000 : null,
    pos_from: undefined,
    pos_to: undefined
  });

  const [mapLocation, setMapLocation] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(
    hasInitialLocation
      ? {
          latitude: initialLat,
          longitude: initialLon,
          address: initialAddress,
        }
      : null
  );

  useEffect(() => {
    if (hasInitialLocation && initialDistance !== null) {
      localStorage.setItem('driverSearchDistance', initialDistance.toString());
      eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, initialDistance);
    }
  }, [hasInitialLocation, initialDistance]);

  const handleLocationChange = useCallback((latitude: number, longitude: number, address: string) => {
    console.log('位置变化:', latitude, longitude, address);
    setMapLocation({ latitude, longitude, address });
    setCenterLabel('当前位置');

    setFilters((currentFilters) => {
      const nextDistance = currentFilters.distance ?? (() => {
        const currentCachedDistance = localStorage.getItem('driverSearchDistance');
        return currentCachedDistance ? Number(currentCachedDistance) : null;
      })();

      const updatedFilters = {
        ...currentFilters,
        lat: latitude,
        lon: longitude,
        distance: nextDistance,
      };

      if (updatedFilters.distance === null) {
        setSnackbarMessage('请输入查询距离');
        setSnackbarOpen(true);
        return updatedFilters;
      }

      setSearchParams({
        ...updatedFilters,
        distance: updatedFilters.distance * 1000,
        pos_from: updatedFilters.pos_from,
        pos_to: updatedFilters.pos_to,
      });
      setPage(1);

      return updatedFilters;
    });
  }, []);

  useEffect(() => {
    const unsubscribe = eventBus.subscribe(
      EVENT_NAMES.MAP_LOCATION_CHANGE,
      handleLocationChange
    );

    return () => unsubscribe();
  }, [handleLocationChange]);

  const handleSearch = () => {
    if (mapLocation && filters.distance === null) {
      setSnackbarMessage('请输入查询距离');
      setSnackbarOpen(true);
      return;
    }

    const updatedParams = {
      ...filters,
      lat: mapLocation?.latitude,
      lon: mapLocation?.longitude,
      distance: filters.distance !== null ? filters.distance * 1000 : null,
      pos_from: filters.pos_from,
      pos_to: filters.pos_to
    };
    setSearchParams(updatedParams);
    setPage(1);
  };

  const handleReset = () => {
    const defaultFilters: FilterType = {
      name: '',
      phone: '',
      plate: '',
      type: '',
      distance: null,
      lat: undefined,
      lon: undefined,
      pos_from: undefined,
      pos_to: undefined
    };
    setFilters(defaultFilters);
    setSearchParams(defaultFilters);
    setPage(1);

    localStorage.removeItem('driverSearchDistance');

    eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, null);
  };

  const { data, total, isLoading } = useGetList('drivers', {
    pagination: { page, perPage },
    sort: { field: 'id', order: 'DESC' },
    filter: {
      name: searchParams.name || undefined,
      phone: searchParams.phone || undefined,
      'trucks.0.plate': searchParams.plate || undefined,
      'trucks.0.type': searchParams.type ? truckTypeOptions.find(option => option.value === searchParams.type)?.label || undefined : undefined,
      ...(searchParams.distance !== null ? { distance: searchParams.distance } : {}),
      ...(searchParams.lat && searchParams.lon ? {
        lat: searchParams.lat,
        lon: searchParams.lon
      } : {}),
      ...(searchParams.pos_from ? { pos_from: searchParams.pos_from } : {}),
      ...(searchParams.pos_to ? { pos_to: searchParams.pos_to } : {}),
    },
  });

  const listContext = useList({ data, isLoading, perPage, page });

  useEffect(() => {
    if (data && !isLoading) {
      eventBus.publish(EVENT_NAMES.DRIVER_DATA_UPDATED, data);
    }
  }, [data, isLoading]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {mapLocation && (
        <Box sx={{ mb: 1, p: 1, backgroundColor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1, whiteSpace: 'nowrap' }}>
              {centerLabel}:
            </Typography>
            <Chip
              size="small"
              label={`${mapLocation.address || '未知地址'} (${mapLocation.latitude.toFixed(4)}, ${mapLocation.longitude.toFixed(4)})`}
              color="primary"
              variant="outlined"
              sx={{ maxWidth: '100%', overflow: 'hidden' }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setMapLocation(null);
              if (searchParams.lat || searchParams.lon) {
                const newParams = { ...searchParams };
                delete newParams.lat;
                delete newParams.lon;
                setSearchParams(newParams);
              }
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '50px' }}
      >
        <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SearchForm
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onReset={handleReset}
        mapLocation={mapLocation}
      />
      <Box sx={{ flex: 1, width: '100%' }}>
        <ListContextProvider value={{ ...listContext, total: total || 0 }}>
          <Datagrid
            rowClick={(id, resource, record) => {
              console.log('司机被选中:', record);
              eventBus.publish(EVENT_NAMES.DRIVER_SELECTED, record);
              return false;
            }}
            bulkActionButtons={false}
            size="small"
            sx={{
              width: '100%',
              '& .RaDatagrid-headerCell': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem',
              },
              '& .RaDatagrid-row': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              },
              '& .MuiTableCell-root': {
                color: 'white',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '6px 16px',
                fontSize: '0.875rem',
              },
              '& .MuiTable-root': {
                width: '100%',
                tableLayout: 'fixed',
              },
            }}
          >
            <TextField source="name" label="姓名" />
            <TextField source="phone" label="手机号" />
            <TextField source="trucks.0.plate" label="车牌" />
            <FunctionField
              source="trucks.0.truck_type"
              label="车型"
              render={(record: Driver) => TruckType(record?.trucks?.[0]?.truck_type)}
            />
            <FunctionField
              source="trucks.0.truck_length"
              label="车长"
              render={(record: Driver) =>
                TruckLength(record?.trucks?.[0]?.truck_length)
              }
            />
          </Datagrid>
          <Pagination
            rowsPerPageOptions={[5, 10, 15, 25]}
            page={page}
            perPage={perPage}
            setPage={setPage}
            setPerPage={setPerPage}
            sx={{
              width: '100%',
              '& .MuiToolbar-root': {
                color: 'white',
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                minHeight: '48px',
                padding: '0 8px',
              },
              '& .MuiTablePagination-root': {
                width: '100%',
              },
              '& .MuiTablePagination-select': {
                color: 'white',
              },
              '& .MuiTablePagination-selectIcon': {
                color: 'white',
              },
              '& .MuiTablePagination-displayedRows': {
                color: 'white',
                fontSize: '0.875rem',
              },
              '& .MuiIconButton-root': {
                color: 'white',
                padding: '4px',
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              },
            }}
          />
        </ListContextProvider>
      </Box>
    </Box>
  );
};

export default DriverListView;
